import {useCallback, useMemo} from 'react'

import {useAnalytics} from '@github-ui/use-analytics'
import {useMarkersDialogContext} from '../contexts/MarkersDialogContext'

type StartConversationDialog = {
  // New conversation
  isNewConversationDialogOpen: boolean
  startNewConversation: () => void
  startNewConversationWithSuggestedChange: () => void
  closeNewConversation: () => void
  shouldStartNewConversationWithSuggestedChange: boolean | undefined
}

export function useStartConversationDialog({
  cellId,
  onOpenDialog,
}: {
  cellId?: string
  onOpenDialog?: () => void
}): StartConversationDialog {
  const {openDialog, closeDialog, openNewConversationCell} = useMarkersDialogContext()
  const {sendAnalyticsEvent} = useAnalytics()

  const isNewConversationDialogOpen = useMemo(
    () => openNewConversationCell?.cellId === cellId,
    [cellId, openNewConversationCell],
  )
  // Internal
  const openNewConversationDialog = useCallback(
    (opts?: {withSuggestedChange?: boolean}) => {
      onOpenDialog?.()
      openDialog('new-conversation', cellId, opts)
    },
    [cellId, onOpenDialog, openDialog],
  )

  const startNewConversation = useCallback(() => {
    openNewConversationDialog()
  }, [openNewConversationDialog])

  const startNewConversationWithSuggestedChange = useCallback(() => {
    openNewConversationDialog({withSuggestedChange: true})
    sendAnalyticsEvent('diff.start_new_conversation_with_suggested_change', 'CELL_CONTEXT_MENU')
  }, [openNewConversationDialog, sendAnalyticsEvent])

  const closeNewConversation = useCallback(() => {
    closeDialog('new-conversation')
  }, [closeDialog])

  const shouldStartNewConversationWithSuggestedChange = useMemo(
    () => openNewConversationCell?.withSuggestedChange,
    [openNewConversationCell],
  )

  return {
    isNewConversationDialogOpen,
    startNewConversation,
    startNewConversationWithSuggestedChange,
    closeNewConversation,
    shouldStartNewConversationWithSuggestedChange,
  }
}
